import React, { useState, useEffect } from 'react';
import Photo from '../../Assets/photo12.png';
import { useNavigate } from 'react-router-dom';
import { isBrowser } from 'react-device-detect';
import { publicIpv4 } from 'public-ip';
import RestApi from '../../Apis/RestApi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import termsPdf from '../../Assets/Smart_QrCodes_TnC.pdf';
import { setAuthCookies } from '../../utils/AuthUtils';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.body.appendChild(script);
    };
    
    loadGoogleScript();

    return () => {
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const initializeGoogleSignIn = () => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleSignInResponse,
      });
      window.google.accounts.id.renderButton(
        document.getElementById('googleSignInButton'),
        { theme: 'outline', size: 'large', text: 'continue_with' }
      );
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // Helper function to properly parse first_login value
  const shouldNavigateToPremium = (firstLogin) => {
    // Convert string values to actual boolean
    if (typeof firstLogin === 'string') {
      return firstLogin.toLowerCase() === 'true';
    }
    // If it's already a boolean, use it directly
    return !!firstLogin;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in both email and password.');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      setIsLoading(true);
      const Address = `${await publicIpv4()}_${isBrowser ? "web" : "mobile"}`;
      let data = { email, password, address: Address };
      const response = await RestApi.signin(data);
      
      if (response.data.status) {
        
        if (response.data.msg === "Successfully logged in.") {
          const { userObj, token, type } = response.data.data;
          
          // Set user data in both cookies and localStorage
          setAuthCookies({
            _id: userObj._id,
            email: userObj.email,
            type: type,
            username: userObj.name || userObj.username,
            first_login: userObj.first_login.toString(),
            token
          });
          
          if (userObj.email_verified === false) {
            toast.error("Email not Verified, Please Verify your email first.");
            setIsLoading(false);
            return;
          }
          
          login(userObj, type);
          toast.success(response.data.msg);
          
          // Fixed navigation logic using the helper function
          const goToPremium = shouldNavigateToPremium(userObj.first_login);
          navigate(goToPremium ? '/premium' : '/home');
        } else {
          toast.error("Invalid Login Credentials");
        }
      } else {
        toast.error(response.data.err || "Internal server Error");
      }
    } catch (error) {
      console.error("Error during login:", error);
      toast.error(error.response?.data?.err || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignInResponse = async (response) => {
    try {
      setIsLoading(true);
      const { credential } = response;
      const address = `${await publicIpv4()}_${isBrowser ? "web" : "mobile"}`;

      const apiResponse = await RestApi.googleSignin({ credential, address });
      if (apiResponse.data.status) {
        const { userObj, token, type } = apiResponse.data.data;
        // Set user data in both cookies and localStorage
        setAuthCookies({
          _id: userObj._id,
          email: userObj.email,
          type: type || 'Free',
          username: userObj.name || userObj.username,
          first_login: userObj.first_login,
          token
        });

        login(userObj, type);
        toast.success(apiResponse.data.msg);
        
        // Fixed navigation logic using the helper function
        const goToPremium = shouldNavigateToPremium(userObj.first_login);
        navigate(goToPremium ? '/premium' : '/home');
      } else {
        toast.error(apiResponse.data.error || "Sign-in failed");
      }
    } catch (error) {
      console.error("Error during Google sign-in:", error);
      toast.error("Unable to Sign In using Google");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="flex w-full max-w-4xl rounded-lg bg-white shadow-lg flex-col md:flex-row h-auto md:h-[75vh]">
        <div className="w-full md:w-1/2 p-8">
          <h2 className="text-3xl font-bold mb-4">Welcome !</h2>

          <form className="space-y-4" onSubmit={handleFormSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm sm:leading-[2.5rem] leading-8 pl-3"
                disabled={isLoading}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm sm:leading-[2.5rem] leading-8 pl-3"
                disabled={isLoading}
              />
            </div>
            
            <div className="text-right">
              <span onClick={() => navigate('/forget-password')} className="font-medium text-indigo-600 cursor-pointer hover:text-indigo-500 text-sm">
                Forgot your password?
              </span>
            </div>
            
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-70"
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6">
            <div id="googleSignInButton"></div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account? 
              <span 
                onClick={() => navigate('/signup')} 
                className="font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer ml-1"
              >
                Sign up
              </span>
            </p>
          </div>
        </div>
        <div className="w-full md:w-1/2 bg-cover bg-center hidden md:block">
          <div className="flex h-full w-full justify-center items-center flex-col">
            <h1 className='font-extrabold text-4xl mb-8 animated-gradient'>Smart QR Codes</h1>
            <img src={Photo} alt="Smart QR Codes" className="w-4/5 object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;